//#region IMPORT
import React, { useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { showLoading, closeLoading } from '../../../../Reducer/LoadingReducer';
import 'dayjs/locale/ko';
import dayjs from 'dayjs';
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';
import ExcelJS from 'exceljs';
import { useSearch1, useSave1 } from './WOR_BASIC_01_QUERY';
import WOR_BASIC_01 from './WOR_BASIC_01';
import {
  mapGetWorBasicR2RowToGrid,
  buildSaveWorBasicU2Requests,
} from './WOR_BASIC_01_Schema';
import useCompanyCustDep from '../../../../hooks/useCompanyCustDep';
//#endregion

function WOR_BASIC_01F(props) {
  //#region 변수
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const searchInfo = useSelector((state) => state.SearchInfo);
  const gridRef1 = useRef();

  const [rowData1, setRowData1] = useState([]);
  const [selectedRowData1, setSelectedRowData1] = useState([]);
  const [changedRowData1, setChangedRowData1] = useState([]);
  const [deletedRows, setDeletedRowData1] = useState([]);
  const selectedRowNosRef1 = useRef(new Set());
  const lastCheckedIndexRef1 = useRef(null);
  const currentRowIndexRef1 = useRef(null);
  /** flushSync 직후에도 저장 시 옛 state를 읽지 않도록 항상 최신 row/changed 유지 */
  const rowDataRef = useRef([]);
  const changedRowDataRef = useRef([]);
  rowDataRef.current = rowData1;
  changedRowDataRef.current = changedRowData1;
  //#endregion

  //#region 커스텀훅
  const { validateSelection } = useCompanyCustDep({
    requireCompany: true,
    requireCust: true,
  });
  //#endregion

  //#region 쿼리 (getWorBasicR2)
  const baseYear = props.selectedYear ?? String(dayjs().year());
  const querySearch1 = useSearch1({
    selectedYear: baseYear,
    onSuccess: (rows) => {
      const mapped = (rows || []).map(mapGetWorBasicR2RowToGrid);
      setRowData1(mapped);
    },
  });

  const ResetLogic = () => {
    setRowData1([]);
    setSelectedRowData1([]);
    setChangedRowData1([]);
    setDeletedRowData1([]);
    gridRef1?.current?.api?.deselectAll();
  };

  const SearchLogic1 = async () => {
    if (!validateSelection()) return;
    ResetLogic();
    await querySearch1.refetch();
  };

  const { runAsync: Save1Async } = useSave1({
    onSuccess: () => {
      dispatch(showSnackbar({ message: '저장되었습니다.', type: 'success' }));
      setChangedRowData1([]);
      querySearch1.refetch();
    },
  });

  const SaveLogic1 = async () => {
    if (!validateSelection()) return;

    const api = gridRef1.current?.api;
    const editingRowNos = new Set();
    (api?.getEditingCells?.() ?? []).forEach((cell) => {
      const rowNode = api?.getDisplayedRowAtIndex?.(cell.rowIndex);
      const no = rowNode?.data?.no;
      if (no != null) editingRowNos.add(no);
    });

    flushSync(() => {
      api?.stopEditing?.(false);
    });

    const rowSnap = rowDataRef.current;
    const changedSnap = changedRowDataRef.current;

    const gridRowByNo = new Map();
    api?.forEachNode?.((node) => {
      const d = node?.data;
      if (d != null && d.no != null) gridRowByNo.set(d.no, { ...d });
    });

    const changedRowNos = new Set([
      ...(changedSnap || []).map((r) => r.no),
      ...editingRowNos,
    ]);

    const mergedChanged = [];
    for (const no of changedRowNos) {
      const base = (rowSnap || []).find((r) => r.no === no);
      const live = gridRowByNo.get(no);
      const c = (changedSnap || []).find((x) => x.no === no);
      if (live) mergedChanged.push({ ...(base || {}), ...(c || {}), ...live, editYn: 'Y' });
      else if (c) mergedChanged.push({ ...c });
    }

    const requests = buildSaveWorBasicU2Requests(mergedChanged, {
      custCd: searchInfo.custCd || '',
      baseYear: baseYear,
      userId: userInfo?.userid ?? userInfo?.userId ?? userInfo?.userNo ?? '',
    });
    if (!requests.length) {
      dispatch(
        showSnackbar({ message: '저장할 데이터가 없습니다.', type: 'warning' })
      );
      return;
    }
    try {
      await Save1Async(requests);
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message || '저장 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    }
  };
  //#endregion

  //#region 인쇄
  const PrintLogic1 = async () => {
    dispatch(showLoading());
    try {
      if (changedRowData1.length > 0) {
        dispatch(
          showSnackbar({
            message: '데이터를 저장 후 인쇄하세요.',
            type: 'warning',
          })
        );
        dispatch(closeLoading());
        return;
      }

      const baseRows = rowData1 || [];
      const changedList = changedRowData1 || [];
      const noInBase = new Set(baseRows.map((r) => r.no));
      const mergedRows = [
        ...baseRows.map((row) => {
          const c = changedList.find((x) => x.no === row.no);
          return c ? { ...row, ...c } : row;
        }),
        ...changedList.filter((c) => !noInBase.has(c.no)),
      ];
      const toPrint = mergedRows.filter((r) => r.deleteYn !== 'Y');

      if (!toPrint.length) {
        dispatch(
          showSnackbar({
            message: '인쇄할 데이터가 없습니다.',
            type: 'warning',
          })
        );
        dispatch(closeLoading());
        return;
      }

      const currentYearNum = parseInt(baseYear, 10) || dayjs().year();
      const todayDate = dayjs().format('YYYY-MM-DD');
      const year1 = currentYearNum - 2;
      const year2 = currentYearNum - 1;
      const year3 = currentYearNum;
      const year4 = currentYearNum + 1;

      const formatCell = (v) => {
        if (v == null || v === '') return '';
        if (v === 'PASS') return 'PASS';
        if (
          typeof v === 'string' &&
          /^\d{4}-\d{2}-\d{2}/.test(v) &&
          dayjs(v).isValid()
        ) {
          return dayjs(v).format('YYYY-MM-DD');
        }
        return String(v);
      };

      const year1Str = `${year1}년`;
      const year2Str = `${year2}년`;
      const year3Str = `${year3}년`;
      const year4Str = `${year4}년`;

      const headerData = {
        currentYear: `${currentYearNum}`,
        todayDate,
        year1: year1Str,
        year2: year2Str,
        year3: year3Str,
        year4: year4Str,
      };

      const rows = toPrint.map((row) => ({
        gongJongNm: row.gongJongNm || '',
        year1FirstHalf: formatCell(row.year1FirstHalf),
        year1SecondHalf: formatCell(row.year1SecondHalf),
        year2FirstHalf: formatCell(row.year2FirstHalf),
        year2SecondHalf: formatCell(row.year2SecondHalf),
        year3FirstHalf: formatCell(row.year3FirstHalf),
        year3SecondHalf: formatCell(row.year3SecondHalf),
        year4FirstHalf: formatCell(row.year4FirstHalf),
        year4SecondHalf: formatCell(row.year4SecondHalf),
        remark: row.remark || '',
        year1: year1Str,
        year2: year2Str,
        year3: year3Str,
        year4: year4Str,
      }));

      const printData = {
        FileName: 'WOR_BASIC_01',
        HeaderData: JSON.stringify(headerData),
        TableData: JSON.stringify(rows),
      };

      /*
       * 레포트(서버 템플릿) 쪽에서 고려할 사항:
       * 1. TableData는 JSON 배열 문자열 → parse 후 반드시 "배열 전체"를 루프(반복)해서 출력해야 함. 첫 번째 요소만 쓰면 한 행만 나옴.
       * 2. 각 행 객체 필드명(camelCase): gongJongNm, year1FirstHalf, year1SecondHalf, year2FirstHalf, year2SecondHalf,
       *    year3FirstHalf, year3SecondHalf, year4FirstHalf, year4SecondHalf, remark, year1, year2, year3, year4
       * 3. HeaderData: currentYear, todayDate, year1, year2, year3, year4 (예: "2024년" 형식)
       * 4. 테이블 본문은 TableData 배열을 순회하는 디테일 밴드/반복 구간으로 설계해야 전체 행이 출력됨.
       */

      await Commons.Axios(
        'report',
        Global.base.useUrl + Global.urls.Reports.reports,
        printData
      ).then((response) => {
        const blobUrl = URL.createObjectURL(response.data);
        window.open(blobUrl, '_blank');
      });
    } catch (e) {
      console.error(e);
      dispatch(
        showSnackbar({
          message: e?.message || '인쇄 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    } finally {
      dispatch(closeLoading());
    }
  };
  //#endregion

  //#region useEffect
  useEffect(() => {
    if (props.filterClick1) {
      gridRef1.current?.api?.setQuickFilter(searchInfo.searchText || '');
      props.setFilterClick1?.(false);
    }
    if (props.saveClick1) {
      SaveLogic1();
      props.setSaveClick1?.(false);
    }
    if (props.printClick1) {
      if (!validateSelection()) {
        props.setPrintClick1?.(false);
        return;
      }
      PrintLogic1();
      props.setPrintClick1?.(false);
    }
    if (props.searchClick1) {
      if (validateSelection()) {
        ResetLogic();
        querySearch1.refetch();
      }
      props.setSearchClick1?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchClick1 등 클릭 플래그에만 반응
  }, [
    props.filterClick1,
    props.saveClick1,
    props.printClick1,
    props.searchClick1,
  ]);
  //#endregion

  const MarkedForDeletion1 = (rowsToMark) => {
    if (!rowsToMark?.length) return;
    const noSet = new Set(rowsToMark.map((r) => r.no));
    const newRowData = rowData1.map((row) =>
      noSet.has(row.no) ? { ...row, deleteYn: 'Y' } : row
    );
    setRowData1(newRowData);
    setChangedRowData1((prev) => {
      const filtered = prev.filter((r) => !noSet.has(r.no));
      const updated = rowsToMark.map((r) => ({
        ...r,
        deleteYn: 'Y',
        editYn: 'Y',
      }));
      return [...filtered, ...updated];
    });
    gridRef1.current?.api?.setRowData?.(newRowData);
  };

  //#region 엑셀 다운로드
  const ExcelDownload1 = async () => {
    try {
      dispatch(showLoading());

      const toPrint = (rowData1 || []).filter((r) => r.deleteYn !== 'Y');
      // if (!toPrint.length) {
      //   dispatch(showSnackbar({ message: '다운로드할 데이터가 없습니다.', type: 'warning' }));
      //   dispatch(closeLoading());
      //   return;
      // }

      const currentYearNum = parseInt(baseYear, 10) || dayjs().year();
      const year1 = currentYearNum - 2;
      const year2 = currentYearNum - 1;
      const year3 = currentYearNum;
      const year4 = currentYearNum + 1;

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('작업환경측정 주기');

      // ── 타이틀 행 (1행) ─────────────────────────────────
      worksheet.mergeCells('A1:J1');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = `${currentYearNum}년 작업환경측정 주기`;
      titleCell.font = { bold: true, name: 'Arial', size: 12 };
      titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD9E1F2' },
      };
      worksheet.getRow(1).height = 24;

      // ── 연도 그룹 헤더 (2행) ─────────────────────────────
      const yearHeaderRow = worksheet.getRow(2);
      [
        { col: 'A', label: '공정명' },
        { col: 'B', label: `${year1}년` },
        { col: 'D', label: `${year2}년` },
        { col: 'F', label: `${year3}년` },
        { col: 'H', label: `${year4}년` },
        { col: 'J', label: '비고' },
      ].forEach(({ col, label }) => {
        const cell = yearHeaderRow.getCell(col);
        cell.value = label;
        cell.font = { bold: true, name: 'Arial', size: 10 };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE2EFDA' },
        };
        applyBorder(cell);
      });
      worksheet.mergeCells('B2:C2');
      worksheet.mergeCells('D2:E2');
      worksheet.mergeCells('F2:G2');
      worksheet.mergeCells('H2:I2');
      yearHeaderRow.height = 20;

      // ── 상/하반기 서브헤더 (3행) ─────────────────────────
      const subHeaderRow = worksheet.getRow(3);
      const subHeaders = [
        { col: 1, label: '공정명' },
        { col: 2, label: '상반기' },
        { col: 3, label: '하반기' },
        { col: 4, label: '상반기' },
        { col: 5, label: '하반기' },
        { col: 6, label: '상반기' },
        { col: 7, label: '하반기' },
        { col: 8, label: '상반기' },
        { col: 9, label: '하반기' },
        { col: 10, label: '비고' },
      ];
      subHeaders.forEach(({ col, label }) => {
        const cell = subHeaderRow.getCell(col);
        cell.value = label;
        cell.font = { bold: true, name: 'Arial', size: 10 };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF2F2F2' },
        };
        applyBorder(cell);
      });
      subHeaderRow.height = 20;

      // 공정명·비고는 2~3행 병합
      worksheet.mergeCells('A2:A3');
      worksheet.mergeCells('J2:J3');

      // ── 컬럼 너비 ─────────────────────────────────────────
      worksheet.columns = [
        { width: 22 }, // 공정명
        { width: 14 },
        { width: 14 }, // year1
        { width: 14 },
        { width: 14 }, // year2
        { width: 14 },
        { width: 14 }, // year3
        { width: 14 },
        { width: 14 }, // year4
        { width: 20 }, // 비고
      ];

      // ── 데이터 행 (4행~) ─────────────────────────────────
      const dataColumnKeys = [
        'gongJongNm',
        'year1FirstHalf',
        'year1SecondHalf',
        'year2FirstHalf',
        'year2SecondHalf',
        'year3FirstHalf',
        'year3SecondHalf',
        'year4FirstHalf',
        'year4SecondHalf',
        'remark',
      ];

      toPrint.forEach((item, index) => {
        const row = worksheet.getRow(4 + index);
        dataColumnKeys.forEach((key, colIdx) => {
          const cell = row.getCell(colIdx + 1);
          cell.value = item[key] || '';
          cell.font = { name: 'Arial', size: 10 };
          cell.alignment = {
            vertical: 'middle',
            horizontal: colIdx === 0 || colIdx === 9 ? 'left' : 'center',
          };
          applyBorder(cell);
        });
        row.height = 18;
      });

      // ── 파일 다운로드 ─────────────────────────────────────
      const dateTimeStr = getCurrentFormattedDateTime('file');
      const fileName = `작업환경측정_${dateTimeStr}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();

      dispatch(closeLoading());
    } catch (error) {
      console.error('Excel download error:', error);
      dispatch(
        showSnackbar({
          message: `엑셀 다운로드 중 오류가 발생했습니다: ${error.message}`,
          type: 'error',
        })
      );
      dispatch(closeLoading());
    }
  };

  function applyBorder(cell) {
    cell.border = {
      top: { style: 'thin', color: { argb: 'FF808080' } },
      left: { style: 'thin', color: { argb: 'FF808080' } },
      bottom: { style: 'thin', color: { argb: 'FF808080' } },
      right: { style: 'thin', color: { argb: 'FF808080' } },
    };
  }

  function getCurrentFormattedDateTime(_type) {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const mins = String(now.getMinutes()).padStart(2, '0');
    const secs = String(now.getSeconds()).padStart(2, '0');
    if (_type === 'file') return `${year}${month}${day}_${hours}${mins}${secs}`;
    return `${year}-${month}-${day} ${hours}:${mins}:${secs}`;
  }
  //#endregion

  //#region 나머지 함수
  const NewYnErrorMsg = () => {
    dispatch(
      showSnackbar({
        message: '데이터 입력중입니다. 저장 후 시도하세요.',
        type: 'warning',
      })
    );
  };

  const handleRowSelection1 = (rowData, rowIndex) => {
    currentRowIndexRef1.current = rowIndex;
    setSelectedRowData1([rowData]);
  };
  //#endregion

  return (
    <div className="wrap">
      <WOR_BASIC_01
        gridId1="grid1"
        baseYear={baseYear}
        gridRef1={gridRef1}
        rowData1={rowData1}
        setRowData1={setRowData1}
        selectedRowData1={selectedRowData1}
        setSelectedRowData1={setSelectedRowData1}
        changedRowData1={changedRowData1}
        setChangedRowData1={setChangedRowData1}
        deletedRowData1={deletedRows}
        selectedRowNosRef1={selectedRowNosRef1}
        lastCheckedIndexRef1={lastCheckedIndexRef1}
        validateSelection={validateSelection}
        searchInfo={searchInfo}
        ResetLogic1={ResetLogic}
        SearchLogic1={SearchLogic1}
        MarkedForDeletion1={MarkedForDeletion1}
        NewYnErrorMsg={NewYnErrorMsg}
        handleRowSelection1={handleRowSelection1}
        ExcelDownload1={ExcelDownload1}
      />
    </div>
  );
}

export default WOR_BASIC_01F;
